import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  FaCode,
  FaDatabase,
  FaChartBar,
  FaGitAlt,
  FaDocker,
  FaChevronDown,
  FaBars,
  FaTimes,
  FaPalette,
  FaMoon,
  FaSun,
} from "react-icons/fa";
import {
  DiJavascript,
  DiPython,
  DiReact,
  DiNodejs,
  DiGit,
  DiMongodb,
  DiCss3,
  DiHtml5,
} from "react-icons/di";
import {
  SiTypescript,
  SiExpress,
  SiTableau,
  SiPowerbi,
  SiMysql,
  SiMicrosoftexcel,
  SiFigma,
  SiPhp,
} from "react-icons/si";
import "../styles/Home.css";
import "../styles/MobileNav.css";
import SearchBar from "../components/SearchBar";
import { useTheme } from "../context/ThemeContext";

const Home = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const { isDark, toggleTheme } = useTheme();

  const allNavItems = [
    { path: "/", label: "Home" },
    { path: "/cheatsheet/html", label: "HTML" },
    { path: "/cheatsheet/css", label: "CSS" },
    { path: "/cheatsheet/javascript", label: "JavaScript" },
    { path: "/cheatsheet/react", label: "React" },
    { path: "/cheatsheet/python", label: "Python" },
    { path: "/cheatsheet/php", label: "PHP" },
    { path: "/cheatsheet/typescript", label: "TypeScript" },
    { path: "/cheatsheet/ux-ui", label: "UX/UI Design" },
    { path: "/cheatsheet/nodejs", label: "Node.js" },
    { path: "/cheatsheet/express", label: "Express" },
    { path: "/cheatsheet/mongodb", label: "MongoDB" },
    { path: "/cheatsheet/sql", label: "SQL" },
    { path: "/cheatsheet/git", label: "Git" },
    { path: "/cheatsheet/docker", label: "Docker" },
    { path: "/cheatsheet/excel", label: "Excel" },
    { path: "/cheatsheet/tableau", label: "Tableau" },
    { path: "/cheatsheet/powerbi", label: "Power BI" },
  ];

  const mainNavItems = allNavItems.slice(0, 7);
  const moreNavItems = allNavItems.slice(7);

  const categories = [
    {
      title: "Web Development",
      description: "Frontend and markup languages",
      icon: FaCode,
      items: [
        {
          title: "HTML",
          description: "Structure and semantic markup",
          icon: DiHtml5,
          path: "/cheatsheet/html",
        },
        {
          title: "CSS",
          description: "Styling and layout",
          icon: DiCss3,
          path: "/cheatsheet/css",
        },
        {
          title: "JavaScript",
          description: "Modern JavaScript features and best practices",
          icon: DiJavascript,
          path: "/cheatsheet/javascript",
        },
        {
          title: "React",
          description: "React components and hooks",
          icon: DiReact,
          path: "/cheatsheet/react",
        },
      ],
    },
    {
      title: "Design & UX",
      description: "User experience and interface design",
      icon: FaPalette,
      items: [
        {
          title: "UX/UI Design",
          description: "Design principles and patterns",
          icon: FaPalette,
          path: "/cheatsheet/ux-ui",
        },
      ],
    },
    {
      title: "Programming & Backend",
      description: "Server-side programming and frameworks",
      icon: FaCode,
      items: [
        {
          title: "Python",
          description: "Python programming fundamentals and advanced concepts",
          icon: DiPython,
          path: "/cheatsheet/python",
        },
        {
          title: "PHP",
          description: "Modern PHP features, patterns, and best practices",
          icon: SiPhp,
          path: "/cheatsheet/php",
        },
        {
          title: "TypeScript",
          description: "TypeScript types and advanced features",
          icon: SiTypescript,
          path: "/cheatsheet/typescript",
        },
        {
          title: "Node.js",
          description: "Node.js runtime and server-side development",
          icon: DiNodejs,
          path: "/cheatsheet/nodejs",
        },
        {
          title: "Express",
          description: "Express web framework and middleware",
          icon: SiExpress,
          path: "/cheatsheet/express",
        },
      ],
    },
    {
      title: "Databases & Storage",
      description: "Database management and storage solutions",
      icon: FaDatabase,
      items: [
        {
          title: "MongoDB",
          description: "MongoDB database operations and queries",
          icon: DiMongodb,
          path: "/cheatsheet/mongodb",
        },
        {
          title: "SQL",
          description: "SQL queries and database management",
          icon: SiMysql,
          path: "/cheatsheet/sql",
        },
      ],
    },
    {
      title: "DevOps & Tools",
      description: "Development tools and deployment",
      icon: FaGitAlt,
      items: [
        {
          title: "Git",
          description: "Version control and Git workflows",
          icon: DiGit,
          path: "/cheatsheet/git",
        },
        {
          title: "Docker",
          description: "Container management and deployment",
          icon: FaDocker,
          path: "/cheatsheet/docker",
        },
      ],
    },
    {
      title: "Data & Analytics",
      description: "Data analysis and visualization tools",
      icon: FaChartBar,
      items: [
        {
          title: "Excel",
          description: "Excel formulas and data analysis",
          icon: SiMicrosoftexcel,
          path: "/cheatsheet/excel",
        },
        {
          title: "Tableau",
          description: "Data visualization and analytics",
          icon: SiTableau,
          path: "/cheatsheet/tableau",
        },
        {
          title: "Power BI",
          description: "Business intelligence and reporting",
          icon: SiPowerbi,
          path: "/cheatsheet/powerbi",
        },
      ],
    },
  ];

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <div className="home-container dark">
      <nav className="top-nav">
        <div className="nav-brand">
          <Link to="/">Himal Block CheatSheets</Link>
        </div>
        <button
          className="theme-toggle"
          onClick={toggleTheme}
          aria-label="Toggle dark mode"
        >
          {isDark ? <FaSun /> : <FaMoon />}
        </button>
        <SearchBar />
        <button className="mobile-menu-button" onClick={toggleMobileMenu}>
          {isMobileMenuOpen ? <FaTimes /> : <FaBars />}
        </button>
        <div className="nav-links">
          {mainNavItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              {item.label}
            </Link>
          ))}
          <div className="more-dropdown">
            <button onClick={() => setShowMore(!showMore)}>
              More <FaChevronDown className={showMore ? "rotate" : ""} />
            </button>
            <div className={`dropdown-content ${showMore ? "show" : ""}`}>
              {moreNavItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    setShowMore(false);
                  }}
                >
                  {item.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </nav>

      {isMobileMenuOpen && (
        <div className="mobile-nav-container">
          <div className="mobile-nav-header">
            <div className="mobile-nav-title">Menu</div>
            <button className="mobile-nav-close" onClick={toggleMobileMenu}>
              <FaTimes />
            </button>
          </div>
          <div className="mobile-nav-links">
            {allNavItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className="mobile-nav-link"
                onClick={toggleMobileMenu}
              >
                {item.label}
              </Link>
            ))}
          </div>
        </div>
      )}

      <main className="main-content">
        {categories.map((category, index) => (
          <section key={index} className="category-section">
            <div className="section-header">
              <category.icon className="section-icon" />
              <div className="section-title">
                <h2>{category.title}</h2>
                <p>{category.description}</p>
              </div>
            </div>
            <div className="category-grid">
              {category.items.map((item, itemIndex) => (
                <Link to={item.path} key={itemIndex} className="category-card">
                  <item.icon className="card-icon" />
                  <div className="card-content">
                    <h3>{item.title}</h3>
                    <p>{item.description}</p>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        ))}
      </main>
    </div>
  );
};

export default Home;
